import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import type { Row } from '@tanstack/react-table'
import { Skeleton, Tooltip } from 'antd'
import React from 'react'

import PriceSegmentsPicker from '@/components/Feed/PriceSegments/PriceSegmentsPicker'
import { getPartnerSegmentHint } from '@/components/Feed/PriceSegments/priceSegmentsTexts'
import SegmentPriceValue from '@/components/Feed/PriceSegments/SegmentPriceValue'
import { useFeedStore } from '@/stores/feed/feedStore'
import { usePriceSegmentsStore } from '@/stores/feed/priceSegmentsStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { FormattedFeedProductType } from '@/types'
import { isBuyerUser } from '@/utils/auth/userRoles'
import { getRowOfferPrice } from '@/utils/products/offerPrice'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import cn from './SegmentPriceColumns.module.scss'

const NO_SEGMENT_PRICE = '—'

const SEGMENT_PRICE_TITLE = 'Цена по сегменту'

export function SegmentPriceHeader() {
  const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))
  const contextSegment = useUserInfoStore((state) => state.contextSegment)
  const visible = useFeedStore((state) => state.isSegmentPriceVisible)
  const setVisible = useFeedStore((state) => state.setIsSegmentPriceVisible)

  if (isClient) return <>{SEGMENT_PRICE_TITLE}</>

  return (
    <span className={cn.segmentHeader}>
      {contextSegment ? (
        <PriceSegmentsPicker
          label={`${SEGMENT_PRICE_TITLE} · ${contextSegment.description}`}
        />
      ) : (
        SEGMENT_PRICE_TITLE
      )}
      <button
        type="button"
        className={cn.eyeButton}
        aria-label={
          visible ? 'Скрыть цену по сегменту' : 'Показать цену по сегменту'
        }
        onClick={(event) => {
          event.stopPropagation()
          setVisible(!visible)
          focusProductSearchInput()
        }}
      >
        {visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </button>
    </span>
  )
}

export const SegmentOfferPriceCell = React.memo(
  ({
    row,
    segmentCode,
  }: {
    row: Row<FormattedFeedProductType>
    segmentCode: number
  }) => {
    const isSegmentPriceVisible = useFeedStore(
      (state) => state.isSegmentPriceVisible
    )
    const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))
    const loading = usePriceSegmentsStore((state) => state.segmentPricesLoading)

    if (!isClient && !isSegmentPriceVisible)
      return <span className={cn.muted}>***</span>

    const price = getRowOfferPrice(row.original)
    const segmentPrices = price?.segmentPrices
    if (!segmentPrices && loading)
      return <Skeleton.Input active size="small" className={cn.skeleton} />
    if (!segmentPrices) {
      const partnerSegment = row.original.suppliersStock[0]?.clientSegment
      return partnerSegment ? (
        <Tooltip title={getPartnerSegmentHint(partnerSegment.description)}>
          <span className={cn.muted}>{NO_SEGMENT_PRICE}</span>
        </Tooltip>
      ) : (
        <span className={cn.muted}>{NO_SEGMENT_PRICE}</span>
      )
    }

    return (
      <SegmentPriceValue
        segmentPrice={segmentPrices.find(
          (item) => item.segmentCode === segmentCode
        )}
        contextPrice={price?.sellingPrice}
      />
    )
  }
)

SegmentOfferPriceCell.displayName = 'SegmentOfferPriceCell'
